import Card from '@/components/cards';
import { ProductLink } from '@/components/products/product';
import Projects from '@/components/projects';
import { getProducts } from '@/data/products';
import { useState, FormEvent, useCallback, useEffect } from "react";
import { useProjects } from "@/contexts/ProjectContext";
import { t } from "i18next";
import { useUser } from "@/contexts/UserContext";
import useFetch from "@/hooks/useFetch";
import { useNotification } from "@/contexts/NotificationContext";

export default function Home() {
  const { projectData } = useProjects();
  const { userData } = useUser();
  const products = getProducts();
  const concrete_providers_prod = products.find(
    (product) => product.classification === "concrete",
  );
  const [linkLoading, setLinkLoading] = useState<boolean>(false);
  const [showContactForm, setShowContactForm] = useState(false);
  const [formData, setFormData] = useState({
    message: "",
    email: "",
  });
  const { execute, status, data, loading } = useFetch<any>({
    url: "/contact/",
    method: "POST",
    body: JSON.stringify(formData),

    immediate: false,
  });
  const { showNotification } = useNotification();

  useEffect(() => {
    if (userData?.email) {
      setFormData({
        message: "",
        email: userData.email,
      });
    }
  }, [userData, setFormData]);

  const handleSubmit = useCallback(
    async (e: FormEvent) => {
      e.preventDefault();

      if (!userData?.email) {
        showNotification({
          type: "error",
          text: t("contact.error"),
          timeout: 5000,
          title: t("common.error"),
        });
        return;
      }

      showNotification({
        type: "info",
        text: t("contact.sending"),
        timeout: 2000,
        title: t("common.loading"),
      });

      await execute();
    },
    [formData, userData?.email, showNotification],
  );

  useEffect(() => {
    if (data) {
      if (status === 200) {
        setShowContactForm(false);
        showNotification({
          type: "success",
          text: t("contact.success"),
          timeout: 5000,
          title: t("common.success"),
        });
      } else {
        showNotification({
          type: "error",
          text: t("contact.error"),
          timeout: 5000,
          title: t("common.error"),
        });
      }
    }
  }, [status, data, showNotification]);

  return (
    <>
      <div className="flex flex-col items-start min-h-screen w-full gap-10">
        {concrete_providers_prod?.enabled && (
          <div className="flex items-start flex-col">
            <h2 className="text-2xl font-bold mb-4">Concrete Providers</h2>

            <div className="flex items-center">
              <ProductLink
                product={concrete_providers_prod}
                target="_blank"
                key={concrete_providers_prod.key}
                setLoading={setLinkLoading}
              >
                <Card
                  props={{
                    name: concrete_providers_prod.name,
                    description: concrete_providers_prod.classification,
                    image: concrete_providers_prod.info_image_url || "",
                    products: [concrete_providers_prod],
                    loading: linkLoading,
                  }}
                />
              </ProductLink>
            </div>
          </div>
        )}

        <div className="flex flex-col w-full">
          {projectData?.length === 0 ? (
            <div className="text-center p-4 bg-gray-50 rounded-lg">
              <p className="text-gray-600 mb-2">
                {t("dashboard.content.home.no_projects")}
              </p>

              <button
                onClick={() => setShowContactForm(true)}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
              >
                {t("contact.help")}
              </button>
            </div>
          ) : (
            <Projects />
          )}
        </div>
      </div>

      {showContactForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center">
          <div className="bg-white p-6 rounded-lg w-96">
            <h2 className="text-xl font-bold mb-4">{t("contact.title")}</h2>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  {t("contact.message")}
                </label>
                <textarea
                  value={formData.message}
                  onChange={(e) =>
                    setFormData({ ...formData, message: e.target.value })
                  }
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm"
                  rows={4}
                  required
                />
              </div>
              <div className="flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setShowContactForm(false)}
                  className="px-4 py-2 text-gray-600 hover:text-gray-800"
                >
                  {t("common.cancel")}
                </button>
                <button
                  type="submit"
                  className={`px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 ${
                    loading
                      ? "opacity-50 cursor-not-allowed animate-pulse hover:bg-blue-600"
                      : ""
                  }`}
                  disabled={loading}
                >
                  {t("contact.send")}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
}
